import * as Sentry from '@sentry/browser';

import parseSectionJson from '../utils/parseSectionJson';

class PhoneForm extends HTMLElement {
  constructor() {
    super();

    this.checkoutRedirect = this.getAttribute('data-checkout-redirect') === 'true';

    // Setup elements
    this.form = this.querySelector('form');
    this.button = this.querySelector('[data-atc-button]');
    this.variantField = this.querySelector('[data-variant-field]');
    this.errorEl = this.querySelector('[data-error]');
    this.phonePriceEl = document.querySelectorAll('[data-phone-price]');
    this.phonePriceCompareEl = document.querySelectorAll('[data-phone-price-compare]');
    this.subtotalEl = this.querySelector('[data-subtotal]');
    this.pricingTableEl = this.querySelector('[data-pricing-table]');
    this.affirmEl = this.querySelector('[data-affirm]');

    // Get the offer options
    const options = JSON.parse(this.querySelector('[data-phone-options]').innerHTML);
    this.variants = options.variants;
    this.plan = options.plan;

    // Set the initial state
    this.selectedVariant = this.variants.find((variant) => variant.id === this.variantField.value);
    this.planSelected = !!this.plan;

    // Bind event handlers
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.form.addEventListener('change', this.handleChange.bind(this));
  }

  formatPrice(value) {
    if (!value || value === 0) return '';

    const formated = value.toFixed(2);
    return `$${formated.replace('.00', '')}`;
  }

  updatePricing() {
    const phonePriceOriginal = this.selectedVariant.price;

    if (this.planSelected) {
      const phonePriceDiscounted = Math.max(phonePriceOriginal - this.plan.discount, 0);
      const planPriceDiscounted = Math.max(this.plan.price - this.plan.discountPlan, 0);
      const subtotal = phonePriceDiscounted + planPriceDiscounted;

      this.phonePriceEl.forEach((el) => {
        el.textContent = phonePriceDiscounted > 0 ? this.formatPrice(phonePriceDiscounted) : 'Free';
      });
      this.phonePriceCompareEl.forEach((el) => {
        el.textContent = phonePriceOriginal > phonePriceDiscounted ? this.formatPrice(phonePriceOriginal) : '';
      });
      this.subtotalEl.textContent = this.formatPrice(subtotal);
      this.affirmEl?.setAttribute('data-amount', subtotal * 100);
      this.pricingTableEl.classList.remove('hidden');
    } else {
      this.phonePriceEl.forEach((el) => {
        el.textContent = this.formatPrice(phonePriceOriginal);
      });
      this.phonePriceCompareEl.forEach((el) => {
        el.textContent = '';
      });
      this.subtotalEl.textContent = this.formatPrice(phonePriceOriginal);
      this.affirmEl?.setAttribute('data-amount', phonePriceOriginal * 100);
      this.pricingTableEl.classList.add('hidden');
    }

    if (this.affirmEl && window.affirm) {
      window.affirm.ui.refresh();
    }
  }

  handleChange() {
    const formData = new FormData(this.form);
    const { option1, option2, option3, plan } = Object.fromEntries(formData.entries());

    // Update the selected variant
    this.selectedVariant = this.variants.find((variant) => {
      return Object.entries({ option1, option2, option3 }).every(([key, value]) => {
        if (!value) return true;
        return variant[key] === value;
      });
    });

    // Dispatch the variant change event
    document.body.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: {
          variant: this.selectedVariant,
        },
      }),
    );

    // Update the form state
    this.variantField.value = this.selectedVariant.id;

    // Update the plan state
    this.planSelected = !!plan;

    this.updatePricing();
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Update the form state
    this.button.disabled = true;
    this.button.classList.add('loading');
    this.errorEl.classList.add('hidden');

    const properties = {};

    if (this.offer) {
      properties['_offer'] = this.offer;
    }

    if (this.offerOriginalSubtotal) {
      properties['_offer_original_subtotal'] = this.offerOriginalSubtotal;
    }

    try {
      const data = {
        items: [],
        sections: ['cart', 'cart-json'],
      };

      // Add the plan to the cart if selected
      if (this.planSelected) {
        data.items = [
          {
            id: this.plan.id,
            quantity: 1,
            properties: {
              _offer: 'device',
              _offer_discount: this.plan.discount,
              _offer_discount_plan: this.plan.discountPlan,
              _offer_device: this.selectedVariant.id,
              _offer_plan: this.plan.id,
              _offer_id: this.plan.offerId,
            },
          },
          {
            id: this.selectedVariant.id,
            quantity: 1,
          },
        ];
      } else {
        data.items.push({
          id: this.selectedVariant.id,
          quantity: 1,
        });
      }

      // Limits the number of plans to 1
      const clearResponse = await fetch(`${window.Shopify.routes.root}cart/clear.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!clearResponse.ok) {
        const responseData = await response.json();
        throw new Error('Clear Cart Failed | ' + JSON.stringify(responseData));
      }

      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error('Add To Cart Failed | ' + JSON.stringify(responseData));
      }

      if (this.checkoutRedirect) {
        window.location = '/checkout';
        return;
      } else {
        const responseData = await response.json();
        this.handleCartResponse(responseData);
      }
    } catch (error) {
      this.handleError(error);
    }

    // Reset the form state
    this.button.disabled = false;
    this.button.classList.remove('loading');
  }

  handleCartResponse(data) {
    document.body.dispatchEvent(
      new CustomEvent('cart:rebuild', {
        detail: {
          html: data.sections.cart,
        },
      }),
    );

    document.body.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: {
          cart: parseSectionJson(data.sections['cart-json']).items,
          shouldOpenCart: true,
        },
      }),
    );
  }

  handleError(error) {
    Sentry.captureException(error);
    console.error('Error:', error);
    this.errorEl.classList.remove('hidden');
  }
}

customElements.define('phone-form', PhoneForm);
